import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search, ChevronDown, ChevronUp, Stethoscope, FileDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReferralBusiness() {
  const token = localStorage.getItem('vendor_token');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});

  const fetchReferrals = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const res = await fetch(`${API}/api/lis/business/referrals?${params}`, { headers: { Authorization: `Bearer ${token}` } });
    setDoctors(await res.json());
  }, [search, dateFrom, dateTo, token]);
  useEffect(() => { fetchReferrals(); }, [fetchReferrals]);

  const toggleExpand = async (name) => {
    if (expanded === name) { setExpanded(null); return; }
    setExpanded(name);
    if (!details[name]) {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await fetch(`${API}/api/lis/business/referrals/${encodeURIComponent(name)}/details?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setDetails(d => ({ ...d, [name]: data }));
    }
  };

  const exportCSV = async (doctorName) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (doctorName) params.append('doctor_name', doctorName);
    const res = await fetch(`${API}/api/lis/business/export/referrals?${params}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doctorName ? `referral_${doctorName.replace(/\s+/g, '_')}.csv` : 'referral_business_export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalRevenue = doctors.reduce((s, d) => s + d.total_revenue, 0);
  const totalComm = doctors.reduce((s, d) => s + d.commission_amount, 0);

  return (
    <div data-testid="lis-referral-business">
      <div className="flex items-center justify-between mb-4">
        <div><h1 className="text-xl font-bold text-slate-800">Referral Business</h1><p className="text-sm text-slate-500">Total Revenue: Rs.{totalRevenue.toLocaleString()} | Commission: Rs.{totalComm.toLocaleString()}</p></div>
        <Button variant="outline" size="sm" onClick={() => exportCSV(null)} data-testid="export-all-referrals-btn"><FileDown className="w-3.5 h-3.5 mr-1" /> Export All</Button>
      </div>
      <Card className="mb-4 border-slate-200"><CardContent className="p-3"><div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]"><Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" /><Input placeholder="Search doctor..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" /></div>
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[150px] h-9" /><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[150px] h-9" />
      </div></CardContent></Card>
      <div className="space-y-3">{doctors.map(d => (
        <Card key={d.doctor_name} className="border-slate-200"><CardContent className="p-0">
          <button onClick={() => toggleExpand(d.doctor_name)} className="w-full p-4 flex items-center justify-between text-left">
            <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center"><Stethoscope className="w-4 h-4 text-teal-600" /></div><div><p className="font-medium text-sm">{d.doctor_name}</p><p className="text-xs text-slate-500">{d.total_cases} cases | Commission: {d.commission_percentage}%</p></div></div>
            <div className="flex items-center gap-4 text-sm"><span className="text-slate-600">Revenue: <strong>Rs.{d.total_revenue.toLocaleString()}</strong></span><span className="text-emerald-600">Commission: <strong>Rs.{d.commission_amount.toLocaleString()}</strong></span>{expanded === d.doctor_name ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</div>
          </button>
          {expanded === d.doctor_name && details[d.doctor_name] && (
            <div className="border-t border-slate-200 p-4">
              <div className="flex justify-end mb-2">
                <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); exportCSV(d.doctor_name); }} data-testid={`export-doctor-${d.doctor_name.replace(/\s+/g, '-')}`} className="h-7 text-xs">
                  <FileDown className="w-3 h-3 mr-1" /> Export {d.doctor_name}
                </Button>
              </div>
              <table className="w-full text-xs"><thead><tr className="border-b bg-slate-50"><th className="p-2 text-left">Reg#</th><th className="p-2 text-left">Date</th><th className="p-2 text-left">Patient</th><th className="p-2 text-right">Total</th><th className="p-2 text-right">B2B</th><th className="p-2 text-right">Paid</th></tr></thead>
              <tbody>{details[d.doctor_name].map(b => (<tr key={b.id} className="border-b border-slate-100"><td className="p-2">#{b.reg_no}</td><td className="p-2">{(b.created_at||'').slice(0,10)}</td><td className="p-2">{b.patient_name}</td><td className="p-2 text-right">Rs.{b.total}</td><td className="p-2 text-right text-blue-600">Rs.{b.total_b2b}</td><td className="p-2 text-right text-emerald-600">Rs.{b.amount_received}</td></tr>))}</tbody></table>
            </div>
          )}
        </CardContent></Card>
      ))}</div>
      {doctors.length === 0 && <Card className="border-dashed"><CardContent className="p-8 text-center text-slate-400">No referral data found.</CardContent></Card>}
    </div>
  );
}
